import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { CreditCard, TrendingUp, TrendingDown, Check, X } from 'lucide-react';

interface Client {
  id: string;
  company_name: string;
  email: string;
  phone: string;
}

interface Profile {
  subscription_status: string;
  subscription_plan: string;
}

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 299,
    features: ['5 Social Media Platforms', '10 Posts/Month', 'Basic Analytics', 'Email Support']
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 599,
    features: ['10 Social Media Platforms', '30 Posts/Month', 'Advanced Analytics', 'Priority Support', 'Content Calendar']
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 1299,
    features: ['Unlimited Platforms', 'Unlimited Posts', 'Advanced Analytics', '24/7 Support', 'Dedicated Manager', 'Custom Integrations']
  }
];

export default function SubscriptionManagement() {
  const { user } = useAuthStore();
  const [client, setClient] = useState<Client | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientRes, profileRes] = await Promise.all([
        supabase.from('clients').select('*').eq('user_id', user?.id).maybeSingle(),
        supabase.from('profiles').select('subscription_status, subscription_plan').eq('user_id', user?.id).maybeSingle()
      ]);

      if (clientRes.data) setClient(clientRes.data);
      if (profileRes.data) setProfile(profileRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const changePlan = async (newPlan: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ subscription_plan: newPlan })
        .eq('user_id', user?.id);

      if (error) throw error;

      alert('Subscription plan change request submitted. Our team will contact you shortly.');
      setShowUpgradeModal(false);
      fetchData();
    } catch (error) {
      console.error('Error changing plan:', error);
      alert('Failed to change plan');
    }
  };

  const currentPlan = plans.find(p => p.id === profile?.subscription_plan);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
        <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          {/* Current Plan */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Current Plan</h2>
                <p className="text-gray-600 mt-1">
                  Status: <span className={`font-medium ${profile?.subscription_status === 'active' ? 'text-green-600' : 'text-gray-600'}`}>
                    {profile?.subscription_status || 'none'}
                  </span>
                </p>
              </div>
              <CreditCard className="w-12 h-12 text-blue-600" />
            </div>

            {currentPlan ? (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{currentPlan.name}</h3>
                    <p className="text-3xl font-bold text-blue-600 mt-2">
                      ${currentPlan.price}<span className="text-lg text-gray-600">/month</span>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <TrendingUp className="w-4 h-4" />
                      Upgrade Plan
                    </button>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="w-full px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                    >
                      <TrendingDown className="w-4 h-4" />
                      Downgrade Plan
                    </button>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Plan Features:</h4>
                  <ul className="space-y-2">
                    {currentPlan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <Check className="w-5 h-5 text-green-600 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <p className="text-yellow-800">No active subscription. Please select a plan below.</p>
              </div>
            )}
          </div>

          {/* Available Plans */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Plans</h2>
            <div className="grid grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                    plan.id === profile?.subscription_plan ? 'ring-2 ring-blue-600' : ''
                  }`}
                >
                  {plan.id === profile?.subscription_plan && (
                    <div className="bg-blue-600 text-white text-center py-2 text-sm font-medium">
                      Current Plan
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-600">/month</span>
                    </div>
                    <ul className="mt-6 space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {plan.id !== profile?.subscription_plan && (
                      <button
                        onClick={() => {
                          setSelectedPlan(plan.id);
                          setShowUpgradeModal(true);
                        }}
                        className="w-full mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                      >
                        Select Plan
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Billing History */}
          <div className="mt-8 bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Billing History</h2>
            <div className="text-center text-gray-500 py-8">
              <CreditCard className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No billing history available yet</p>
            </div>
          </div>
        </>
      )}

      {/* Change Plan Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Change Subscription Plan</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to change your subscription plan? Our team will review your request and contact you shortly.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Plan changes will be effective from the next billing cycle. You will receive a confirmation email once the change is processed.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => changePlan(selectedPlan || currentPlan?.id || 'starter')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
